import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import * as Dialog from '@radix-ui/react-dialog'
import { format } from 'date-fns'
import { useMemo } from 'react'

import type { CalendarEvent } from './Calendar'
import CalendarTile from './CalendarTile'
import type { SelectedTile } from './CalendarModal'

export interface AgendaPaneProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The day whose posts are listed. */
  date?: Date
  events: CalendarEvent[]
  /** Open the post preview for a card; the pane closes first (no stacked modals). */
  onPostClick: (selectedTile: SelectedTile) => void
  isReadOnly: boolean
}

const toSelectedTile = (event: CalendarEvent): SelectedTile => ({
  id: event.id.toString(),
  date: format(new Date(event.start), 'MMM d yyyy'),
  time: event.tileData.time,
  platform: event.tileData.platform,
  status: event.tileData.status,
})

/**
 * SKE-3795 — the day agenda pane (Figma node 3598:65080), reduced to the
 * **Posts** section only. External review has no Notes/Ideas, post labels,
 * content pillars or "+ add" affordances, so those internal-only elements are
 * excluded. Opens from a calendar cell's "N more" trigger and lists that day's
 * posts grouped by time; selecting a card opens the existing CalendarModal.
 *
 * Composed from Radix Dialog primitives (a direct dependency) rather than the
 * kit's centred DialogContent, so it can dock to the right as a full-height
 * sheet while keeping focus-trap / Esc / scroll-lock behaviour.
 */
const AgendaPane: React.FC<AgendaPaneProps> = ({
  open,
  onOpenChange,
  date,
  events,
  onPostClick,
  isReadOnly,
}) => {
  const timeGroups = useMemo(() => {
    const sorted = [...events].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    )
    const map = new Map<string, CalendarEvent[]>()
    for (const event of sorted) {
      const time = event.tileData.time
      const group = map.get(time)
      if (group) group.push(event)
      else map.set(time, [event])
    }
    return [...map.entries()]
  }, [events])

  const handleCardClick = (event: CalendarEvent) => {
    onOpenChange(false)
    onPostClick(toSelectedTile(event))
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1000] bg-black/40 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed right-0 top-0 z-[1001] flex h-full w-[360px] max-w-[90vw] flex-col bg-white shadow-[-4px_0_24px_rgba(16,24,40,0.12)] outline-none',
            'data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          )}
        >
          {/* Header — date + close */}
          <div className="flex items-center justify-between bg-[#fbfafd] px-4 py-3">
            <Dialog.Title className="m-0 font-inter text-sm font-semibold text-neutral-800">
              {date ? format(date, 'EEE d MMM yyyy') : 'Posts'}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="grid size-6 cursor-pointer place-items-center rounded-lg border-0 bg-transparent text-neutral-800 outline-none hover:bg-neutral-50"
            >
              <i aria-hidden="true" className="fa-regular fa-xmark text-base" />
            </Dialog.Close>
          </div>

          {/* Posts list */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="mb-2 flex items-center gap-2">
              <i
                aria-hidden="true"
                className="fa-regular fa-file text-base text-neutral-800"
              />
              <span className="font-inter text-sm font-semibold text-neutral-800">
                Posts
              </span>
            </div>

            {timeGroups.length === 0 ? (
              <p className="mt-6 text-center text-sm text-neutral-500">
                No posts scheduled.
              </p>
            ) : (
              timeGroups.map(([time, groupEvents]) => (
                <div key={time} className="mb-4">
                  {/* Time divider pill */}
                  <div className="mb-2 flex items-center gap-2">
                    <span className="h-px flex-1 bg-neutral-100" />
                    <span className="rounded-[22px] border border-neutral-100 px-2 py-1 text-[10px] font-normal text-neutral-500">
                      {time}
                    </span>
                    <span className="h-px flex-1 bg-neutral-100" />
                  </div>

                  <div className="flex flex-col gap-2">
                    {groupEvents.map((event) => (
                      <CalendarTile
                        key={event.id}
                        {...event.tileData}
                        showCaption
                        isReadOnly={isReadOnly}
                        data-testid={`agenda-tile-${event.id}`}
                        onClick={() => handleCardClick(event)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default AgendaPane
